/**
 * Reference data, defined the same way documents are.
 *
 * A master is a simpler document: a flat row, no lines, no approval chain, and
 * a soft delete instead of a cancellation. One generic service and one generic
 * screen serve all of them, so adding a master is a definition and nothing else.
 */
import type { ControlKind, FieldDef } from '../documents/types.js'

export type MasterGroup =
  | 'Items and materials'
  | 'Tools'
  | 'Parties'
  | 'Addresses and contacts'
  | 'Commercial'
  | 'People and teams'
  | 'Documents'
  | 'Drive'

export interface MasterColumnDef {
  key: string
  label: string
  type?: 'text' | 'money' | 'qty' | 'rate' | 'date' | 'boolean' | 'code'
  width?: number
  align?: 'left' | 'right'
}

export interface MasterDef {
  key: string
  route: string
  group: MasterGroup
  label: string
  labelPlural: string
  blurb: string
  /**
   * The tenant table this edits. It is spliced into SQL as an identifier, so
   * the registry IS the allow-list — nothing outside it can ever be reached.
   */
  table: string
  /** Columns searched by the list box and by every type-ahead. */
  searchColumns: readonly string[]
  orderBy?: string
  /**
   * Values forced on read and on write. `party` holds customers, suppliers and
   * subcontractors in one table separated by boolean flags; this is what keeps
   * the Customers screen showing only customers.
   */
  fixed?: Readonly<Record<string, boolean | string>>
  columns: readonly MasterColumnDef[]
  fields: readonly FieldDef[]
  /** Masters are retired, never deleted — documents still reference them. */
  softDelete?: boolean
}

export const defineMaster = <const T extends MasterDef>(def: T): T => def

/** Shorthand for the flat field shapes masters use. */
export const m = {
  text: (key: string, label: string, required = false, width: 'full' | 'half' = 'half'): FieldDef =>
    ({ key, label, control: 'text', required, width }),
  area: (key: string, label: string): FieldDef => ({ key, label, control: 'textarea', width: 'full' }),
  num: (key: string, label: string, control: ControlKind = 'number', required = false): FieldDef =>
    ({ key, label, control, required, width: 'half' }),
  look: (key: string, label: string, entity: string, required = false): FieldDef =>
    ({ key, label, control: 'lookup', required, width: 'half', lookup: { entity } }),
  choice: (key: string, label: string, options: ReadonlyArray<[string, string]>, required = true): FieldDef =>
    ({ key, label, control: 'select', required, width: 'half', options: options.map(([value, l]) => ({ value, label: l })) }),
  flag: (key: string, label: string, hint?: string): FieldDef =>
    ({ key, label, control: 'checkbox', width: 'half', ...(hint ? { hint } : {}) }),
  date: (key: string, label: string): FieldDef => ({ key, label, control: 'date', width: 'half' }),
}
